// BuyerCostCalculator — full replica of Sean Knudsen's "Temporary Buydown
// Calculator" Excel workbook (Sean Buydown Calc - Loan Summary). Every
// number here should track the workbook cell-for-cell so the buyer sees
// the same figures their lender would quote from the sheet.
//
// The workbook is Utah-specific (First American title schedule, Sean's
// standard fee stack). Contact card at top comes from the workbook
// header (Sean Knudsen NMLS 265807).
import React, { useEffect, useMemo, useState } from "react";
import { Calculator, Home, HelpCircle, ChevronDown, ChevronRight, Phone, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useContent, DEFAULT_CONTENT } from "../lib/content";

// ---------- number helpers -------------------------------------------------
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.round(n || 0));
const fmt2 = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtMonthly = (n) => fmt(n) + "/mo";

// Excel-parity ROUNDDOWN / ROUNDUP helpers.
const rDown = (v, d = 0) => {
  const m = Math.pow(10, d);
  return Math.floor(num(v) * m) / m;
};
const rUp = (v, d = 0) => {
  const m = Math.pow(10, d);
  return Math.ceil(num(v) * m) / m;
};
const round2 = (v) => Math.round(num(v) * 100) / 100;

// PMT: same sign convention as Excel's -PMT (returns positive payment).
const pmt = (rate, nper, pv) => {
  if (!Number.isFinite(rate) || !Number.isFinite(nper) || !Number.isFinite(pv) || nper <= 0) return 0;
  if (rate === 0) return pv / nper;
  return (pv * rate) / (1 - Math.pow(1 + rate, -nper));
};

// ---------- domain constants (mirror workbook `Data` tab) ------------------
const LOAN_TYPES = ["Conventional", "FHA", "USDA", "VA"];
const OCCUPANCIES = ["Primary Residence", "Second Home", "Investment Property"];
const BUYDOWNS = ["No Temp Buydown", "3 - 2 - 1", "2 - 1", "1.5 - 0.5", "1 - 1", "1 - 0"];
const VA_ENTITLEMENT = ["First Use", "Subsequent Use"];
const VA_DISABILITY = ["Not Disabled", "Disabled"];

const FHA_UFMIP = 0.0175;          // Data B17
const USDA_UPFRONT = 0.01010101;   // Data B20
const CONFORMING_LIMIT = 726200;   // Data M76

// Fee defaults from Sean's Loan Summary worksheet (Loan Summary - Fee Worksheet)
const DEFAULT_FEES = {
  lender: 915,           // O19 — Processing/Credit/Tax Service/Flood Cert/MERS
  appraisal: 550,        // O25
  titleFees: 370,        // O27 (settlement + closing protection + doc mgmt + recording pieces)
  recording: 80,         // O31
  secondMortgageFee: 250,// O39 if 2nd mortgage > 0
  realtorTransactionFee: 495, // I12
};
const INSURANCE_ESCROW_MONTHS = 3; // F49
const TAX_ESCROW_MONTHS = 4;       // F51

// Sean's contact was the workbook default (Loan Summary !O2:O4). Every
// tenant now overrides this via /admin/content → BUY SIDE → Lender.
const DEFAULT_LO = DEFAULT_CONTENT.lender;

// ---------- blank form state (matches Transaction Info inputs) -------------
const BLANK = {
  // Transaction Info
  borrower_name: "",
  settlement_date: "",              // YYYY-MM-DD
  loan_type: "Conventional",        // E6
  occupancy: "Primary Residence",   // E7
  price: "",                        // E8
  dp_type: "%",                     // E9   ('%' or '$')
  dp_amount: "5",                   // E10
  interest_rate: "7.125",           // E13  (annual %)
  loan_years: 30,                   // E14
  buydown: "No Temp Buydown",       // E16
  va_entitlement: "First Use",      // E17
  va_disability: "Not Disabled",    // E18
  // Monthly payment inputs (Transaction Info D22-D27)
  property_taxes_monthly: "",       // D24
  home_insurance_monthly: "",       // D25
  second_mortgage_monthly: "",      // D26
  hoa_monthly: "",                  // D27
  conventional_mi_monthly: "",      // D23 (only used when Conventional; buyer's lender quotes exact PMI)
  // Transaction Credits (I4-I6)
  seller_concessions: "",           // I4
  earnest_money: "",                // I5
  lender_credit: "",                // I6
  // Transaction Fees (I12-I15)
  misc_fees: "",                    // I13
  hoa_transfer_fee: "",             // I14
  discount_points: "",              // I15
  // Debts (I25)
  debts_paid_at_closing: "",        // I25
};

// ---------- Utah title-insurance base premium (Calc V2 !P10:P20) ----------
// Graduated rate schedule per $10k. This computes the base "T22 / S22"
// tiered premium given a coverage amount.
const TITLE_BRACKETS = [
  { upTo: 10000,     base: 220, perTen: 0 },      // flat 220 for first bracket
  { upTo: 50000,     base: 220, perTen: 43.18 },
  { upTo: 100000,    base: null,perTen: 51.43 },
  { upTo: 200000,    base: null,perTen: 46.75 },
  { upTo: 500000,    base: null,perTen: 37.40 },
  { upTo: 2000000,   base: null,perTen: 18.70 },
  { upTo: 5000000,   base: null,perTen: 16.50 },
  { upTo: 10000000,  base: null,perTen: 14.03 },
  { upTo: 50000000,  base: null,perTen: 11.55 },
  { upTo: 75000000,  base: null,perTen: 8.80 },
  { upTo: Infinity,  base: null,perTen: 7.15 },
];
// Excel uses roundup(amount/10000) "units" and stacks $220 + perTen * units.
// The premium is the FIRST bracket ($220) + running per-ten adders for every
// $10k unit above 1 up to the applicable bracket. Sean's S/T columns select
// exactly one bracket based on `units` (roundup) — this mirrors that.
function tieredTitlePremium(amount) {
  const amt = num(amount);
  if (amt <= 0) return 0;
  const units = Math.ceil(amt / 10000);                   // Excel ROUNDUP(amt/10000, 0)
  // brackets by [minUnits, maxUnits] and cumulative $ formulas from Calc V2
  // ranges P10:P20 :
  //   units == 1        -> 220
  //   2..5   -> 220 + 43.18*(u-1)
  //   6..10  -> prev(units=5) + 51.43*(u-5)
  //   11..20 -> prev(u=10)   + 46.75*(u-10)
  //   21..50 -> prev(u=20)   + 37.40*(u-20)
  //   51..200-> prev(u=50)   + 18.70*(u-50)
  //   201..500 -> prev(200)  + 16.50*(u-200)
  //   501..1000-> prev(500)  + 14.03*(u-500)
  //   1001..5000-> prev(1000)+ 11.55*(u-1000)
  //   5001..7500-> prev(5000)+ 8.80*(u-5000)
  //   7501+   -> prev(7500)  + 7.15*(u-7500)
  const P10 = 220, P11 = 172.72, P12 = 257.15, P13 = 467.5, P14 = 374, P15 = 2805, P16 = 4950, P17 = 7015, P18 = 46200, P19 = 22000;
  // NOTE: P11..P19 are ROUNDUP( (M-K)/10000 * O , 0 ). Recompute exactly:
  const P = {
    P10,
    P11: Math.ceil(((50000 - 10000) / 10000) * 43.18),   // = 173
    P12: Math.ceil(((100000 - 50000) / 10000) * 51.43),  // = 258
    P13: Math.ceil(((200000 - 100000) / 10000) * 46.75), // = 468
    P14: Math.ceil(((500000 - 200000) / 10000) * 37.40), // = 1122
    P15: Math.ceil(((2000000 - 500000) / 10000) * 18.70),// = 2805
    P16: Math.ceil(((5000000 - 2000000) / 10000) * 16.50),
    P17: Math.ceil(((10000000 - 5000000) / 10000) * 14.03),
    P18: Math.ceil(((50000000 - 10000000) / 10000) * 11.55),
    P19: Math.ceil(((75000000 - 50000000) / 10000) * 8.80),
  };
  if (units === 1) return P.P10;
  if (units <= 5)  return P.P10 + 43.18 * (units - 1);
  if (units <= 10) return P.P10 + P.P11 + 51.43 * (units - 5);
  if (units <= 20) return P.P10 + P.P11 + P.P12 + 46.75 * (units - 10);
  if (units <= 50) return P.P10 + P.P11 + P.P12 + P.P13 + 37.40 * (units - 20);
  if (units <= 200) return P.P10 + P.P11 + P.P12 + P.P13 + P.P14 + 18.70 * (units - 50);
  if (units <= 500) return P.P10 + P.P11 + P.P12 + P.P13 + P.P14 + P.P15 + 16.50 * (units - 200);
  if (units <= 1000) return P.P10 + P.P11 + P.P12 + P.P13 + P.P14 + P.P15 + P.P16 + 14.03 * (units - 500);
  if (units <= 5000) return P.P10 + P.P11 + P.P12 + P.P13 + P.P14 + P.P15 + P.P16 + P.P17 + 11.55 * (units - 1000);
  if (units <= 7500) return P.P10 + P.P11 + P.P12 + P.P13 + P.P14 + P.P15 + P.P16 + P.P17 + P.P18 + 8.80 * (units - 5000);
  return P.P10 + P.P11 + P.P12 + P.P13 + P.P14 + P.P15 + P.P16 + P.P17 + P.P18 + P.P19 + 7.15 * (units - 7500);
}

// Eagle Lender Policy for a purchase (Calc V2 !G12) — the amount buyer pays.
// = MAX(220, ROUNDUP( ROUNDUP(baseTiered) * 0.6818 ) * 1.1 )
function eagleLenderPolicy(loanAmt) {
  if (num(loanAmt) <= 0) return 0;
  const base = Math.ceil(tieredTitlePremium(loanAmt));
  const step = Math.ceil(base * 0.6818);
  return Math.max(220, Math.ceil(step * 1.1));
}

// ---------- VA funding fee (Data A24:A27, B30, B31) ------------------------
function vaFundingFeeRate(entitlement, disability, dpDollars, price) {
  if (disability === "Disabled") return 0;
  const p = num(price);
  if (p <= 0) return 0;
  const dp = num(dpDollars);
  if (dp >= p * 0.10) return 0.0125;   // 10%+ down: 1.25% (First & Subsequent)
  if (dp >= p * 0.05) return 0.015;    // 5-9.99%:   1.50%
  // <5% down: First Use 2.15%, Subsequent 3.30%
  return entitlement === "First Use" ? 0.0215 : 0.033;
}

// ---------- FHA monthly MI (Amortization !Q20-Q48 mapping) ----------------
// Simplified to the applicable annual MI rate. Sean's workbook uses avg UPB
// year-1 which is ~loan_amount for year 1; we use base loan amount which
// matches Sean's year-1 figure to within a few dollars.
function fhaAnnualMiRate(baseLoan, years, ltv) {
  const largeLoan = baseLoan > CONFORMING_LIMIT;
  if (years > 15) {
    if (!largeLoan) {
      // Loan ≤ conforming
      if (ltv > 0.95) return 0.0055;   // >95% -> 55bps
      return 0.0050;                    // ≤95% -> 50bps
    }
    // Loan > conforming (jumbo FHA)
    if (ltv > 0.95) return 0.0075;
    return 0.0070;
  }
  // ≤15 year term
  if (!largeLoan) {
    if (ltv > 0.90) return 0.0040;     // >90%
    if (ltv > 0.78) return 0.0015;     // 78%-90%
    return 0.0015;                     // ≤78%
  }
  // ≤15y, jumbo
  if (ltv > 0.90) return 0.0065;
  if (ltv > 0.78) return 0.0040;
  return 0.0015;
}

// ---------- Temporary buydown tables (Data A145:H188) ----------------------
// Each entry lists the rate DELTA per year and how many months apply. Year
// counts total to term*12. The workbook's H column also sums the buyer's
// year-N total payment; we compute that in the main calc.
const BUYDOWN_TABLE = {
  "3 - 2 - 1":   [{ delta: -0.03, months: 12 }, { delta: -0.02, months: 12 }, { delta: -0.01, months: 12 }, { delta:  0,    months: null }],
  "2 - 1":       [{ delta: -0.02, months: 12 }, { delta: -0.01, months: 12 }, { delta:  0,    months: null }],
  "1.5 - 0.5":   [{ delta: -0.015,months: 12 }, { delta: -0.005,months: 12 }, { delta:  0,    months: null }],
  "1 - 1":       [{ delta: -0.01, months: 12 }, { delta: -0.01, months: 12 }, { delta:  0,    months: null }],
  "1 - 0":       [{ delta: -0.01, months: 12 }, { delta:  0,    months: null }],
  "No Temp Buydown": [{ delta: 0, months: null }],
};

// ---------- days to end of month (Data A106:A109) -------------------------
function daysPrepaidInterest(settlementDateStr) {
  if (!settlementDateStr) return 15;
  const d = new Date(settlementDateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d < today) return 15;            // A109 default
  const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const diff = Math.round((eom - d) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff + 1);        // A108: eom - date, workbook adds +1 for prepaid coverage
}

// ---------- master calc ----------------------------------------------------
function calc(f) {
  const price = num(f.price);
  const rateAnnual = num(f.interest_rate) / 100;   // e.g. 0.07125
  const rateMonthly = rateAnnual / 12;
  const years = num(f.loan_years) || 30;
  const nper = years * 12;

  // Down payment $ (Data A6)
  const dpDollars = f.dp_type === "%"
    ? price * (num(f.dp_amount) / 100)
    : num(f.dp_amount);

  const baseLoan = Math.max(0, rDown(price - dpDollars, 0));       // E11
  const ltv = price > 0 ? baseLoan / price : 0;                     // Data A94

  // Total loan (E12) — Conventional stays base; FHA/USDA add upfront MI;
  // VA adds funding fee.
  let totalLoan = baseLoan;
  let vaFundingFee = 0;
  let fhaUFMIP = 0;
  let usdaUpfront = 0;
  if (f.loan_type === "FHA") {
    fhaUFMIP = rDown(baseLoan * FHA_UFMIP, 0);
    totalLoan = baseLoan + fhaUFMIP;
  } else if (f.loan_type === "USDA") {
    usdaUpfront = rDown(baseLoan * USDA_UPFRONT, 0);
    totalLoan = baseLoan + usdaUpfront;
  } else if (f.loan_type === "VA") {
    const feeRate = vaFundingFeeRate(f.va_entitlement, f.va_disability, dpDollars, price);
    vaFundingFee = rDown(baseLoan * feeRate, 0);
    totalLoan = baseLoan + vaFundingFee;
  }

  // Full-rate P&I (E15) using the TOTAL loan amount (with financed MIP/FF)
  const fullRatePI = pmt(rateMonthly, nper, totalLoan);

  // MI Monthly (D22/E23) — depends on loan type
  let miMonthly = 0;
  if (f.loan_type === "FHA" || f.loan_type === "USDA") {
    // Use base loan × annual rate / 12 (matches Sean's year-1 avg UPB closely)
    const annualRate = f.loan_type === "USDA"
      ? 0.0035
      : fhaAnnualMiRate(baseLoan, years, ltv);
    miMonthly = (baseLoan * annualRate) / 12;
    // FHA "Corrected" strips out UFMIP contribution: /(1+0.0175)
    if (f.loan_type === "FHA") miMonthly = miMonthly / (1 + FHA_UFMIP);
    miMonthly = round2(miMonthly);
  } else if (f.loan_type === "Conventional") {
    miMonthly = num(f.conventional_mi_monthly);
  }

  const monthlyTaxes = num(f.property_taxes_monthly);
  const monthlyIns   = num(f.home_insurance_monthly);
  const monthlyHOA   = num(f.hoa_monthly);
  const monthly2nd   = num(f.second_mortgage_monthly);

  // Buydown schedule — Year 1..N breakdown.
  const schedule = BUYDOWN_TABLE[f.buydown] || BUYDOWN_TABLE["No Temp Buydown"];
  let paymentsUsed = 0;
  const buydownYears = schedule.map((step, idx) => {
    const yrRate = rateAnnual + step.delta;
    const yrPI = pmt(yrRate / 12, nper, totalLoan);
    const savings = fullRatePI - yrPI;
    const months = step.months === null ? Math.max(0, nper - paymentsUsed) : step.months;
    paymentsUsed += months;
    return {
      yearLabel: idx === schedule.length - 1 && schedule.length > 1
        ? `Year ${idx + 1}+`
        : `Year ${idx + 1}`,
      rate: yrRate,
      pi: yrPI,
      savings,
      months,
      annualSavings: savings * months,
    };
  });
  const buydownCost = buydownYears.reduce((sum, y) => sum + Math.max(0, y.annualSavings), 0);
  const yr1PI = buydownYears[0].pi;

  // Full breakdown of monthly payment (year 1, per workbook rows 21-28)
  const totalMonthlyYr1 = yr1PI + miMonthly + monthlyTaxes + monthlyIns + monthly2nd + monthlyHOA;

  // ---------- Fees & prepaids (Loan Summary - Fee Worksheet) --------------
  const lenderFees = DEFAULT_FEES.lender;                                          // O19
  const originationDiscount = num(f.discount_points);                              // O21 = I15
  const tempBuydownCost = buydownCost;                                             // O23 = I16 = Data C98
  const appraisalFee = DEFAULT_FEES.appraisal;                                     // O25
  const titleFees = DEFAULT_FEES.titleFees;                                        // O27
  const titleInsurance = eagleLenderPolicy(totalLoan);                             // O29
  const recordingFees = DEFAULT_FEES.recording;                                    // O31
  const hoaTransferFee = num(f.hoa_transfer_fee);                                  // O33
  const hoaDuesAtClose = monthlyHOA * 2;                                           // O35 (2mo dues)
  const miscFees = num(f.misc_fees);                                               // O37
  const secondMortgageFees = monthly2nd > 0 ? DEFAULT_FEES.secondMortgageFee : 0;  // O39
  const realtorTransactionFee = DEFAULT_FEES.realtorTransactionFee;                // O41

  const totalClosingFees = lenderFees + originationDiscount + tempBuydownCost
    + appraisalFee + titleFees + titleInsurance + recordingFees
    + hoaTransferFee + hoaDuesAtClose + miscFees + secondMortgageFees
    + realtorTransactionFee;

  // Prepaids
  const days = daysPrepaidInterest(f.settlement_date);
  const dailyInterest = (totalLoan * rateAnnual) / 365;
  const prepaidInterest = days * dailyInterest;                                    // O45
  const prepaidInsurance = monthlyIns * 12;                                        // O47
  const insuranceEscrow = monthlyIns * INSURANCE_ESCROW_MONTHS;                    // O49
  const taxEscrow = monthlyTaxes * TAX_ESCROW_MONTHS;                              // O51
  const totalPrepaids = prepaidInterest + prepaidInsurance + insuranceEscrow + taxEscrow;

  // Credits
  const sellerConcessions = num(f.seller_concessions);
  const earnestMoney = num(f.earnest_money);
  const lenderCredit = num(f.lender_credit);
  const totalCredits = sellerConcessions + earnestMoney + lenderCredit;

  // Cash to close (matches Transaction Info I24:I28)
  const downCash = price - baseLoan;                                               // I24
  const debts = num(f.debts_paid_at_closing);                                      // I25
  // Standard Closing Fees / Prepaids / Escrows (I17) = lender + appraisal +
  //   title + title-ins + recording + hoa-dues + 2nd-mtg-fee + prepaids
  // (Realtor, Misc, HOA-transfer, Discount, Temp buydown break out on their
  //  own rows in the Transaction Info credits panel.)
  const standardClosing = lenderFees + appraisalFee + titleFees + titleInsurance
    + recordingFees + hoaDuesAtClose + secondMortgageFees + totalPrepaids;
  const transactionFees = realtorTransactionFee + miscFees + hoaTransferFee
    + originationDiscount + tempBuydownCost + standardClosing;                     // I18
  const totalCashToClose = downCash + debts - totalCredits + transactionFees;      // I28

  return {
    // inputs echo
    baseLoan, totalLoan, dpDollars, ltv,
    fhaUFMIP, usdaUpfront, vaFundingFee,
    // monthly
    fullRatePI, yr1PI, miMonthly, monthlyTaxes, monthlyIns, monthlyHOA, monthly2nd,
    totalMonthlyYr1,
    buydownYears, buydownCost,
    // fees itemized
    lenderFees, originationDiscount, tempBuydownCost,
    appraisalFee, titleFees, titleInsurance, recordingFees,
    hoaTransferFee, hoaDuesAtClose, miscFees, secondMortgageFees, realtorTransactionFee,
    totalClosingFees,
    // prepaids
    days, dailyInterest, prepaidInterest, prepaidInsurance, insuranceEscrow, taxEscrow, totalPrepaids,
    // credits & cash
    sellerConcessions, earnestMoney, lenderCredit, totalCredits,
    downCash, debts, standardClosing, transactionFees, totalCashToClose,
  };
}

// ---------- UI helpers -----------------------------------------------------
function Help({ text }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button" onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        style={{ background: "none", border: "none", padding: "0 4px", cursor: "help", verticalAlign: "middle" }}
        aria-label="What's this?"
      >
        <HelpCircle size={12} color="var(--text-muted)" />
      </button>
      {open && (
        <span style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", marginBottom: 6, background: "#1C1917", color: "#fff", padding: "8px 12px", borderRadius: 6, fontSize: 12, lineHeight: 1.4, width: 260, zIndex: 20, textAlign: "left", pointerEvents: "none" }}>
          {text}
        </span>
      )}
    </span>
  );
}
function Field({ label, help, children }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ fontSize: 13, fontWeight: 500, display: "inline-flex", alignItems: "center", marginBottom: 6 }}>
        {label}{help && <Help text={help} />}
      </span>
      {children}
    </label>
  );
}
function Row({ label, value, testid, hideIfZero = false, v, help = "", bold = false }) {
  if (hideIfZero && (!v || v <= 0)) return null;
  return (
    <div data-testid={testid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", fontSize: 14 }}>
      <span style={{ color: bold ? "var(--text)" : "var(--text-muted)", display: "inline-flex", alignItems: "center", fontWeight: bold ? 600 : 400 }}>
        {label}
        {help && <Help text={help} />}
      </span>
      <b>{value}</b>
    </div>
  );
}
function Section({ title, testid, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ padding: 20, background: "var(--surface-alt)", boxShadow: "none" }} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text)" }}
      >
        <span className="overline">{title}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}

// ---------- main component -------------------------------------------------
export default function BuyerCostCalculator() {
  const [f, setF] = useState(BLANK);
  const [hydrated, setHydrated] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/forms/draft/buyer_cost");
        if (data && data.draft && data.draft.data) {
          setF({ ...BLANK, ...data.draft.data });
        }
      } catch { /* no draft yet */ }
      finally { setHydrated(true); }
    })();
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const t = setTimeout(async () => {
      try {
        await api.put("/forms/draft/buyer_cost", { data: f });
        setSaveMsg("Saved");
        setTimeout(() => setSaveMsg(""), 1400);
      } catch { /* silent */ }
    }, 700);
    return () => clearTimeout(t);
  }, [f, hydrated]);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const c = useMemo(() => calc(f), [f]);
  // Preferred lender is tenant-editable via /admin/content → BUY SIDE →
  // Lender. Fall back to the workbook default (Sean) if a tenant hasn't
  // saved their own. Any blank field is hidden gracefully in the card.
  const { content } = useContent();
  const lo = { ...DEFAULT_LO, ...(content?.lender || {}) };

  const isVA = f.loan_type === "VA";
  const showBuydownTable = f.buydown !== "No Temp Buydown" && num(f.price) > 0;

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
        <div>
          <span className="overline">Buyer estimate · Loan Summary</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 0", letterSpacing: "-0.01em" }}>
            What will this home actually cost you?
          </h1>
          <p style={{ color: "var(--text-muted)", marginTop: 8, maxWidth: 640 }}>
            {"Fee-by-fee estimate modeled on a standard buydown worksheet. Punch in what you know; the numbers match what your lender would quote."}
          </p>
        </div>
        {saveMsg && (
          <div data-testid="buyer-cost-save-msg" style={{ fontSize: 12, color: "var(--primary)", background: "var(--surface-alt)", padding: "6px 10px", borderRadius: 999 }}>{saveMsg}</div>
        )}
      </div>

      {/* Loan officer card — tenant-configurable via /admin/content. Any
          blank sub-field (e.g. missing phone) is skipped so agents can
          share only the contact channels they want to expose. */}
      {(lo.name || lo.phone || lo.email) && (
      <div className="card" data-testid="bc-lender-card" style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 24, background: "var(--surface-alt)" }}>
        <div>
          <div className="overline">Your preferred lender</div>
          {lo.name && <div style={{ fontWeight: 600, marginTop: 4 }}>{lo.name}</div>}
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            {lo.title}{lo.title && lo.company ? " · " : ""}{lo.company}
            {lo.nmls ? ` · NMLS ${lo.nmls}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13 }}>
          {lo.phone && <a href={`tel:${lo.phone}`} data-testid="bc-lender-phone" style={{ color: "var(--primary)", display: "inline-flex", gap: 6, alignItems: "center" }}><Phone size={14} /> {lo.phone}</a>}
          {lo.email && <a href={`mailto:${lo.email}`} data-testid="bc-lender-email" style={{ color: "var(--primary)", display: "inline-flex", gap: 6, alignItems: "center" }}><Mail size={14} /> {lo.email}</a>}
        </div>
      </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.3fr) minmax(320px, 1fr)", gap: 24, marginTop: 24 }}>
        {/* ------------------- Inputs ------------------- */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="buyer-cost-form">
          <Section title="Transaction info" testid="bc-section-transaction">
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Purchase price" help="What you're paying for the home.">
                <input data-testid="bc-price" className="input-field" type="number" min="0" step="1000" value={f.price} onChange={upd("price")} placeholder="500000" />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="Loan type">
                  <select data-testid="bc-loan-type" className="input-field" value={f.loan_type} onChange={upd("loan_type")}>
                    {LOAN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Occupancy">
                  <select data-testid="bc-occupancy" className="input-field" value={f.occupancy} onChange={upd("occupancy")}>
                    {OCCUPANCIES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Loan term">
                  <select data-testid="bc-term" className="input-field" value={f.loan_years} onChange={(e) => setF({ ...f, loan_years: Number(e.target.value) })}>
                    {[30, 25, 20, 15, 10].map((y) => <option key={y} value={y}>{y} years</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Down payment type">
                  <select data-testid="bc-dp-type" className="input-field" value={f.dp_type} onChange={upd("dp_type")}>
                    <option value="%">%</option>
                    <option value="$">$</option>
                  </select>
                </Field>
                <Field label={`Down payment ${f.dp_type === "%" ? "(%)" : "($)"}`}>
                  <input data-testid="bc-dp-amount" className="input-field" type="number" min="0" step={f.dp_type === "%" ? 0.5 : 1000} value={f.dp_amount} onChange={upd("dp_amount")} />
                </Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Interest rate (annual %)">
                  <input data-testid="bc-rate" className="input-field" type="number" min="0" step="0.001" value={f.interest_rate} onChange={upd("interest_rate")} />
                </Field>
                <Field label="Projected settlement date" help="Drives prepaid interest (days from close to end of month).">
                  <input data-testid="bc-settlement-date" className="input-field" type="date" value={f.settlement_date} onChange={upd("settlement_date")} />
                </Field>
              </div>
              <Field label="Temporary buydown" help="Seller-paid rate reduction for the first 1-3 years.">
                <select data-testid="bc-buydown" className="input-field" value={f.buydown} onChange={upd("buydown")}>
                  {BUYDOWNS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              {isVA && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Field label="VA entitlement">
                    <select data-testid="bc-va-entitlement" className="input-field" value={f.va_entitlement} onChange={upd("va_entitlement")}>
                      {VA_ENTITLEMENT.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="VA disability">
                    <select data-testid="bc-va-disability" className="input-field" value={f.va_disability} onChange={upd("va_disability")}>
                      {VA_DISABILITY.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
              )}
            </div>
          </Section>

          <Section title="Monthly expenses" testid="bc-section-monthly">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Property taxes ($/mo)"><input data-testid="bc-taxes" className="input-field" type="number" min="0" step="10" value={f.property_taxes_monthly} onChange={upd("property_taxes_monthly")} placeholder="205" /></Field>
              <Field label="Homeowners insurance ($/mo)"><input data-testid="bc-ins" className="input-field" type="number" min="0" step="10" value={f.home_insurance_monthly} onChange={upd("home_insurance_monthly")} placeholder="80" /></Field>
              <Field label="HOA ($/mo)"><input data-testid="bc-hoa" className="input-field" type="number" min="0" step="10" value={f.hoa_monthly} onChange={upd("hoa_monthly")} placeholder="0" /></Field>
              <Field label="2nd mortgage / other ($/mo)"><input data-testid="bc-2nd" className="input-field" type="number" min="0" step="10" value={f.second_mortgage_monthly} onChange={upd("second_mortgage_monthly")} placeholder="0" /></Field>
              {f.loan_type === "Conventional" && (
                <Field label="Conventional MI ($/mo)" help="Your lender quotes exact PMI. Leave 0 if putting 20%+ down.">
                  <input data-testid="bc-conv-mi" className="input-field" type="number" min="0" step="10" value={f.conventional_mi_monthly} onChange={upd("conventional_mi_monthly")} placeholder="0" />
                </Field>
              )}
            </div>
          </Section>

          <Section title="Credits" testid="bc-section-credits" defaultOpen={false}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="Seller / realtor concessions ($)"><input data-testid="bc-seller-concessions" className="input-field" type="number" min="0" step="100" value={f.seller_concessions} onChange={upd("seller_concessions")} placeholder="0" /></Field>
              <Field label="Earnest money ($)"><input data-testid="bc-earnest" className="input-field" type="number" min="0" step="100" value={f.earnest_money} onChange={upd("earnest_money")} placeholder="5000" /></Field>
              <Field label="Lender credit ($)"><input data-testid="bc-lender-credit" className="input-field" type="number" min="0" step="100" value={f.lender_credit} onChange={upd("lender_credit")} placeholder="0" /></Field>
            </div>
          </Section>

          <Section title="Additional fees & debts" testid="bc-section-extra" defaultOpen={false}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Discount / permanent buydown ($)"><input data-testid="bc-discount-pts" className="input-field" type="number" min="0" step="100" value={f.discount_points} onChange={upd("discount_points")} placeholder="0" /></Field>
              <Field label="Miscellaneous fees ($)" help="Inspections, surveys, termite, one-off costs."><input data-testid="bc-misc-fees" className="input-field" type="number" min="0" step="50" value={f.misc_fees} onChange={upd("misc_fees")} placeholder="0" /></Field>
              <Field label="HOA transfer / reinvestment fee ($)"><input data-testid="bc-hoa-transfer" className="input-field" type="number" min="0" step="50" value={f.hoa_transfer_fee} onChange={upd("hoa_transfer_fee")} placeholder="0" /></Field>
              <Field label="Debts paid off at closing ($)"><input data-testid="bc-debts" className="input-field" type="number" min="0" step="100" value={f.debts_paid_at_closing} onChange={upd("debts_paid_at_closing")} placeholder="0" /></Field>
            </div>
          </Section>
        </div>

        {/* ------------------- Summary ------------------- */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="bc-summary">
          {/* Monthly payment (Yr 1) */}
          <div className="card" style={{ padding: 20, background: "var(--surface-alt)", boxShadow: "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Calculator size={14} style={{ color: "var(--primary)" }} />
              <div className="overline">Monthly payment · Year 1</div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 6 }}>
              <span className="font-serif" style={{ fontSize: 20 }}>Total</span>
              <span className="font-serif" data-testid="bc-monthly-total" style={{ fontSize: 32, color: "var(--primary)", fontWeight: 600 }}>{fmt(c.totalMonthlyYr1)}</span>
            </div>
            <div style={{ marginTop: 8 }}>
              <Row label="Principal & interest (Yr 1)" value={fmtMonthly(c.yr1PI)} testid="bc-monthly-pi" />
              {(f.loan_type === "FHA" || f.loan_type === "USDA") && <Row label={`${f.loan_type} MI`} value={fmtMonthly(c.miMonthly)} testid="bc-monthly-fha-mi" hideIfZero v={c.miMonthly} />}
              {f.loan_type === "Conventional" && c.miMonthly > 0 && <Row label="Conventional MI" value={fmtMonthly(c.miMonthly)} testid="bc-monthly-conv-mi" />}
              <Row label="Property taxes"        value={fmtMonthly(c.monthlyTaxes)} testid="bc-monthly-taxes" hideIfZero v={c.monthlyTaxes} />
              <Row label={"Homeowner\u2019s insurance"} value={fmtMonthly(c.monthlyIns)}  testid="bc-monthly-ins" hideIfZero v={c.monthlyIns} />
              <Row label="2nd mortgage / other"    value={fmtMonthly(c.monthly2nd)} testid="bc-monthly-2nd" hideIfZero v={c.monthly2nd} />
              <Row label="HOA"                    value={fmtMonthly(c.monthlyHOA)} testid="bc-monthly-hoa" hideIfZero v={c.monthlyHOA} />
            </div>
          </div>

          {/* Buydown breakdown (only when applicable) */}
          {showBuydownTable && (
            <div className="card" style={{ padding: 20, background: "var(--surface-alt)", boxShadow: "none" }} data-testid="bc-buydown-table">
              <div className="overline">{f.buydown} buydown</div>
              <table style={{ width: "100%", marginTop: 10, fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: "var(--text-muted)", textAlign: "right" }}>
                    <th style={{ textAlign: "left", padding: "6px 4px", fontWeight: 500 }}>Year</th>
                    <th style={{ padding: "6px 4px", fontWeight: 500 }}>Rate</th>
                    <th style={{ padding: "6px 4px", fontWeight: 500 }}>P&amp;I</th>
                    <th style={{ padding: "6px 4px", fontWeight: 500 }}>Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {c.buydownYears.map((y, i) => (
                    <tr key={i} data-testid={`bc-buydown-row-${i}`} style={{ borderTop: "1px solid var(--border)", textAlign: "right" }}>
                      <td style={{ textAlign: "left", padding: "6px 4px" }}>{y.yearLabel}</td>
                      <td style={{ padding: "6px 4px" }}>{(y.rate * 100).toFixed(3)}%</td>
                      <td style={{ padding: "6px 4px" }}>{fmt(y.pi)}/mo</td>
                      <td style={{ padding: "6px 4px", color: y.savings > 0 ? "var(--primary)" : "var(--text-muted)" }}>{y.savings > 0 ? fmt(y.annualSavings) : "—"}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid var(--border)", fontWeight: 600 }}>
                    <td colSpan={3} style={{ textAlign: "left", padding: "8px 4px" }}>Total buydown cost (seller-paid)</td>
                    <td data-testid="bc-buydown-cost" style={{ textAlign: "right", padding: "8px 4px", color: "var(--primary)" }}>{fmt(c.buydownCost)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Cash to close */}
          <div className="card" style={{ padding: 20, background: "var(--surface-alt)", boxShadow: "none" }} data-testid="bc-cash-summary">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Home size={14} style={{ color: "var(--primary)" }} />
              <div className="overline">Cash to close</div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 6 }}>
              <span className="font-serif" style={{ fontSize: 20 }}>Total due</span>
              <span className="font-serif" data-testid="bc-cash-to-close" style={{ fontSize: 26, color: "var(--primary)", fontWeight: 600 }}>{fmt(c.totalCashToClose)}</span>
            </div>
            <div style={{ marginTop: 10 }}>
              <Row label="Down payment" value={fmt(c.downCash)} testid="bc-down-payment" bold />
              <Row label="Debts paid at closing" value={fmt(c.debts)} testid="bc-debts-out" hideIfZero v={c.debts} />
              <Row label="− Credits" value={c.totalCredits > 0 ? `− ${fmt(c.totalCredits)}` : fmt(0)} testid="bc-credits-out" hideIfZero v={c.totalCredits} />
              <Row label="Realtor transaction fee" value={fmt(c.realtorTransactionFee)} testid="bc-realtor-fee" />
              <Row label="Miscellaneous fees" value={fmt(c.miscFees)} testid="bc-misc-out" hideIfZero v={c.miscFees} />
              <Row label="HOA transfer fee" value={fmt(c.hoaTransferFee)} testid="bc-hoa-transfer-out" hideIfZero v={c.hoaTransferFee} />
              <Row label="Discount / permanent buydown" value={fmt(c.originationDiscount)} testid="bc-discount-out" hideIfZero v={c.originationDiscount} />
              <Row label="Temporary buydown cost" value={fmt(c.tempBuydownCost)} testid="bc-buydown-out" hideIfZero v={c.tempBuydownCost} />
              <Row label="Standard closing / prepaids / escrows" value={fmt(c.standardClosing)} testid="bc-standard-out" bold />
            </div>
          </div>

          {/* Loan summary */}
          <div className="card" style={{ padding: 20, background: "var(--surface-alt)", boxShadow: "none" }} data-testid="bc-loan-summary">
            <div className="overline">Loan summary</div>
            <div style={{ marginTop: 10 }}>
              <Row label="Base loan amount" value={fmt(c.baseLoan)} testid="bc-base-loan" />
              <Row label="Total loan amount" value={fmt(c.totalLoan)} testid="bc-total-loan" help="Base loan + any financed mortgage insurance / VA funding fee." />
              {c.fhaUFMIP > 0 && <Row label="FHA upfront MIP (financed)" value={fmt(c.fhaUFMIP)} testid="bc-fha-ufmip" />}
              {c.usdaUpfront > 0 && <Row label="USDA upfront guarantee (financed)" value={fmt(c.usdaUpfront)} testid="bc-usda-upfront" />}
              {c.vaFundingFee > 0 && <Row label="VA funding fee (financed)" value={fmt(c.vaFundingFee)} testid="bc-va-fee" />}
              <Row label="LTV" value={`${(c.ltv * 100).toFixed(2)}%`} testid="bc-ltv" />
              <Row label="Full-rate P&I" value={fmtMonthly(c.fullRatePI)} testid="bc-full-rate-pi" />
            </div>
          </div>
        </div>
      </div>

      {/* Fee worksheet — full itemized like Sean's Loan Summary sheet */}
      <div className="card" style={{ padding: 24, marginTop: 24 }} data-testid="bc-fee-worksheet">
        <div className="overline">Detailed fee worksheet</div>
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "6px 0 16px" }}>
          {"Every line item as it appears on your lender\u2019s Loan Summary. This is NOT a Loan Estimate \u2014 your actual rate, payment, and costs could differ. Your lender\u2019s LE is the source of truth."}
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <tbody>
            <tr style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "10px 6px", color: "var(--text-muted)" }}>Purchase price</td>
              <td style={{ padding: "10px 6px", textAlign: "right", fontWeight: 600 }}>{fmt(num(f.price))}</td>
            </tr>
            <tr style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "10px 6px", color: "var(--text-muted)" }}>Base loan amount</td>
              <td style={{ padding: "10px 6px", textAlign: "right" }}>{fmt(c.baseLoan)}</td>
            </tr>
            <tr style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "10px 6px", color: "var(--text-muted)" }}>Total loan amount (with financed MIP if applicable)</td>
              <td style={{ padding: "10px 6px", textAlign: "right", fontWeight: 600 }}>{fmt(c.totalLoan)}</td>
            </tr>
            <tr style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "10px 6px", color: "var(--text-muted)" }}>Down payment</td>
              <td style={{ padding: "10px 6px", textAlign: "right" }}>{fmt(c.downCash)}</td>
            </tr>

            <tr><td colSpan={2} style={{ padding: "14px 6px 6px", fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Closing fees</td></tr>
            <FeeRow label="Lender fees (Processing / Credit / Tax Service / Flood Cert / MERS)" value={c.lenderFees} testid="fw-lender" />
            <FeeRow label="Origination / discount points" value={c.originationDiscount} testid="fw-discount" hide={c.originationDiscount <= 0} />
            <FeeRow label="Temporary rate buydown" value={c.tempBuydownCost} testid="fw-buydown" hide={c.tempBuydownCost <= 0} />
            <FeeRow label="Appraisal fee" value={c.appraisalFee} testid="fw-appraisal" />
            <FeeRow label="Title fees (settlement + closing protection + doc mgmt)" value={c.titleFees} testid="fw-title-fees" />
            <FeeRow label="Title insurance (Eagle Lender Policy)" value={c.titleInsurance} testid="fw-title-ins" />
            <FeeRow label="Recording fees" value={c.recordingFees} testid="fw-recording" />
            <FeeRow label="HOA transfer / reinvestment fee" value={c.hoaTransferFee} testid="fw-hoa-transfer" hide={c.hoaTransferFee <= 0} />
            <FeeRow label="HOA dues (2 months)" value={c.hoaDuesAtClose} testid="fw-hoa-dues" hide={c.hoaDuesAtClose <= 0} />
            <FeeRow label="Miscellaneous fees (survey / termite / inspections)" value={c.miscFees} testid="fw-misc" hide={c.miscFees <= 0} />
            <FeeRow label="2nd mortgage closing fees" value={c.secondMortgageFees} testid="fw-2nd-fees" hide={c.secondMortgageFees <= 0} />
            <FeeRow label="Realtor transaction fee" value={c.realtorTransactionFee} testid="fw-realtor" />
            <FeeRow label="Total closing fees" value={c.totalClosingFees} testid="fw-total-closing" bold />

            <tr><td colSpan={2} style={{ padding: "14px 6px 6px", fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Prepaids / escrows</td></tr>
            <FeeRow label={`Prepaid interest (${c.days} days @ ${fmt2(c.dailyInterest)}/day)`} value={c.prepaidInterest} testid="fw-prepaid-int" />
            <FeeRow label="Prepaid insurance premium (1 yr)" value={c.prepaidInsurance} testid="fw-prepaid-ins" hide={c.prepaidInsurance <= 0} />
            <FeeRow label={`Insurance escrow reserves (${INSURANCE_ESCROW_MONTHS} months)`} value={c.insuranceEscrow} testid="fw-ins-escrow" hide={c.insuranceEscrow <= 0} />
            <FeeRow label={`Tax escrow reserves (${TAX_ESCROW_MONTHS} months)`} value={c.taxEscrow} testid="fw-tax-escrow" hide={c.taxEscrow <= 0} />
            <FeeRow label="Total prepaids / escrows" value={c.totalPrepaids} testid="fw-total-prepaids" bold />

            <tr><td colSpan={2} style={{ padding: "14px 6px 6px", fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Credits</td></tr>
            <FeeRow label="Lender credit" value={c.lenderCredit} testid="fw-lender-credit" hide={c.lenderCredit <= 0} negative />
            <FeeRow label="Seller / realtor credits" value={c.sellerConcessions} testid="fw-seller-credit" hide={c.sellerConcessions <= 0} negative />
            <FeeRow label="Earnest money" value={c.earnestMoney} testid="fw-earnest" hide={c.earnestMoney <= 0} negative />
            <FeeRow label="Total credits" value={-c.totalCredits} testid="fw-total-credits" bold hide={c.totalCredits <= 0} />
            <FeeRow label="Debts paid off at closing" value={c.debts} testid="fw-debts" hide={c.debts <= 0} />

            <tr style={{ borderTop: "2px solid var(--border)", background: "var(--surface-alt)" }}>
              <td style={{ padding: "14px 6px", fontWeight: 700, fontSize: 15 }}>Total due at closing</td>
              <td data-testid="fw-total-due" style={{ padding: "14px 6px", textAlign: "right", fontWeight: 700, fontSize: 18, color: "var(--primary)" }}>{fmt(c.totalCashToClose)}</td>
            </tr>
          </tbody>
        </table>

        <p style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 16, lineHeight: 1.5 }}>
          Results are hypothetical and may not be accurate. This is not a commitment to lend. Fee stack modeled on the Utah First American title rate schedule and standard lender fees. Rate, APR, and monthly payment vary by credit, LTV, and market — confirm with your lender.
        </p>
      </div>
    </div>
  );
}

function FeeRow({ label, value, testid, hide = false, bold = false, negative = false }) {
  if (hide) return null;
  const v = negative ? -Math.abs(value) : value;
  return (
    <tr data-testid={testid} style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "8px 6px", color: bold ? "var(--text)" : "var(--text-muted)", fontWeight: bold ? 600 : 400 }}>{label}</td>
      <td style={{ padding: "8px 6px", textAlign: "right", fontWeight: bold ? 700 : 500, color: negative ? "var(--success)" : "inherit" }}>
        {negative && value > 0 ? `− ${fmt(Math.abs(v))}` : fmt(v)}
      </td>
    </tr>
  );
}
